import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { get as _get } from 'lodash';

import { makeStyles } from '@mui/styles';
import { alpha } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Collapse from '@mui/material/Collapse';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/MoreVert';
import HelpIcon from '@mui/icons-material/Help';

import Auth from '../../data/auth-service';
import DataContext from '../../data/data-context';
import POLICY_NAMES from '../../data/policies';

const drawerWidth = '300px';
const drawerWidthCollapsed = '60px !important';

const useStyles = makeStyles(theme => ({
    drawer: {
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        // backgroundColor: theme.palette.
        backgroundColor: theme.palette.nav.paper,
        color: theme.palette.nav.textDrawer,
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
        transition: 'width 500ms',
    },
    drawerCollapsed: {
        width: drawerWidthCollapsed,
        transition: 'width 500ms',
    },
    drawerPaper: {
        width: drawerWidth,
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
        marginLeft: '60px',
        background: theme.palette.nav.paper,
        transition: 'margin-left 500ms, width 500ms',
    },
    drawerPaperExtended: {
        marginLeft: '300px',
        transition: 'margin-left 500ms, width 500ms',
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: theme.spacing(0, 1),
        minHeight: 85,
        ...theme.mixins.toolbar,
    },
    hightlightPrimary: {
        color: theme.palette.nav.text,
    },
    hightlightSecondary: {
        color: theme.palette.secondary.main,
    },
    list: {
        flex: 1,
        width: '100%',
        minWidth: 275,
        maxWidth: 300,
        marginTop: 12,
        color: theme.palette.nav.text,
        '& .MuiListItem-button, & .MuiListItemButton-root': {
            borderBottom: `1px solid ${alpha(theme.palette.nav.text, 0.15)}`,
            paddingTop: '10px',
            paddingBottom: '10px',
            minHeight: 53,
            '& div.MuiListItemIcon-root': {
                color: theme.palette.nav.text,
            }
        },
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
    },
    listItem: {
        color: theme.palette.nav.text,
    },
    listItemPadding: {
        minWidth: '16px',
        width: '16px',
    },
    policyColor: {
        width: '20px',
        height: '20px',
        marginRight: '10px',
    },
    customIcon: {
        width: 22,
        height: 22,
    },
    policyText: {
        cursor: 'pointer',
    },
    policyTextPrimary: {
        fontSize: '14px !important',
        color: theme.palette.nav.text,
    },
    policyTextSecondary: {
        fontSize: '12px !important',
        color: theme.palette.nav.text,
    },
    PolicySecondaryAction: {
        color: theme.palette.nav.text,
    },
    tooltip: {
        color: theme.palette.common.white,
    },
    widthFix: {
        minWidth: '40px',
    }
}));

const PolicySidebar = (props) => {
    const classes = useStyles();
    // const [policyInterval, setPolicyInterval] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const { systemSettings, sidenav, setSidenav } = useContext(DataContext);
    const [panels, setPanels] = useState({
        suricata: false,
        zeek: false,
        bricata: false,
    });
    const [selectedPolicy, setSelectedPolicy] = useState({ type: '', name: '' });
    const [policies, setPolicies] = useState({
        suricata: [],
        zeek: [],
        bricata: [],
    });
    const [header, setHeader] = useState();
    const policyPerms = {
        bro_ti: Auth.permissions.get('ZeekScriptsPolicy'),
        custom_geoip: Auth.permissions.get('GeoIPPolicy'),
        eexportd_conf: Auth.permissions.get('ExportsPolicy'),
        pcap_config: Auth.permissions.get('PcapPolicy'),
        data_tags: Auth.permissions.get('DataTuningPolicy'),
        file_hash: Auth.permissions.get('FileHashPolicy'),
        system_config: Auth.permissions.get('ConfigPolicy'),
        sensor_upgrade: Auth.permissions.get('UpgradePolicy'),
        suricata_rules: Auth.permissions.get('SuricataRulesPolicy'),
        suricata_config: Auth.permissions.get('SuricataVarsPolicy'),
        zeek_indicators: Auth.permissions.get('ThreatIntelPolicy'),
    };

    useEffect(() => {
        setSidenav({ sidebar: 'open' });

        return () => {
            setSidenav({ sidebar: undefined });
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setPolicies({
            bricata: props.policies.filter(v => !v.name.toLowerCase().includes('suricata') && !v.name.toLowerCase().includes('zeek') && !v.name.toLowerCase().includes('threat')),
            suricata: props.policies.filter(v => v.name.toLowerCase().includes('suricata')),
            zeek: props.policies.filter(v => v.name.toLowerCase().includes('zeek') || v.name.toLowerCase().includes('threat')),
        });
    }, [props]);

    useEffect(() => {
        setHeader(!!_get(systemSettings, 'ui.messages.header'));
    }, [systemSettings]);

    const handleNavCollapse = () => {
        setSidenav({
            sidebar: sidenav.sidebar === 'open' ? 'collapsed' : 'open',
        });
        setPanels({});
    };

    const handleOpenPolicySidebarMenu = (e, type, name) => {
        setSelectedPolicy({ type: type, name: name });
        setMenuAnchor(e.currentTarget);
    };

    const handleClosePolicySudebarMenu = () => {
        setSelectedPolicy({ type: '', name: '' });
        setMenuAnchor(null);
    };

    const handleSelectPolicy = (type, name) => {
        props.onSelectPolicy(type, name);
    };

    const handleAddToFiltered = () => {
        props.onAddToFiltered(selectedPolicy);
        setMenuAnchor(null);
    };

    const handleAddToAll = () => {
        props.onAddToAll(selectedPolicy);
        setMenuAnchor(null);
    };

    const handleUpdatePanels = (menu, submenu) => {
        if (menu) {
            setSidenav({
                sidebar: 'open',
            });
        }
        if (submenu) {
            setPanels({
                [menu]: true,
                [submenu]: !panels[submenu],
            });
        } else {
            setPanels({
                [menu]: !panels[menu],
            });
        }
    };

    return (
        <Drawer
            open={true}
            className={clsx({
                [classes.drawer]: true,
                [classes.drawerCollapsed]: open.collapsed,
            })}
            variant="persistent"
            classes={{
                paper: clsx({
                    [classes.drawerPaper]: true,
                    [classes.drawerCollapsed]: sidenav.sidebar === 'collapsed',
                    [classes.drawerPaperExtended]: !sidenav.collapsed,
                }),
            }}
        >
            <div className={classes.toolbar} style={{ marginTop: header ? 30 : 0 }}></div>
            <List component="nav" className={classes.list} aria-label="Side Nav Bar">
                <ListItem button onClick={() => { handleNavCollapse() }} aria-label="Expand or Collpase Side Policy Bar">
                    <ListItemIcon className={classes.widthFix}>
                        {sidenav.sidebar === 'collapsed' ? <NavigateNextIcon onClick={(e) => { handleNavCollapse(false) }} /> : <NavigateBeforeIcon onClick={(e) => { handleNavCollapse(true) }} />}
                    </ListItemIcon>
                    <ListItemSecondaryAction>
                        <Tooltip
                            classes={{ tooltip: classes.tooltip }}
                            title="Add sensors by using the menu options. It may take up to a minute to reflect the changes. Group affiliation count may be inaccurate for sensors that are offline."
                        ><HelpIcon /></Tooltip>
                    </ListItemSecondaryAction>
                </ListItem>
                {Object.keys(policies).map((x, i) => {
                    let display = false;

                    switch (x) {
                        case 'suricata':
                            display = policyPerms.suricata_rules.read || policyPerms.suricata_config.read;
                            break;
                        case 'zeek':
                            display = policyPerms.bro_ti.read || policyPerms.zeek_indicators.read;
                            break;
                        default:
                            display = policyPerms.custom_geoip.read ||
                            policyPerms.eexportd_conf.read ||
                            policyPerms.pcap_config.read ||
                            policyPerms.data_tags.read ||
                            policyPerms.file_hash.read ||
                            policyPerms.system_config.read ||
                            policyPerms.sensor_upgrade.read;
                    }
                    return display ? (
                        <React.Fragment key={'policy-sidebar-group-' + i}>
                            <ListItemButton onClick={(e) => { handleUpdatePanels(x) }} aria-label={'Expand or Collapse ' + x + ' sub-menu'}>
                                <ListItemIcon>
                                    {(() => {
                                        if (x === 'suricata') {
                                            return <img className={classes.customIcon} src={'/images/icon-suricata.png'} />;
                                        } else if (x === 'zeek') {
                                            return <img className={classes.customIcon} src={'/images/icon-zeek.png'} />;
                                        } else {
                                            return <img className={classes.customIcon} src={'/images/icon-new.png'} />;
                                        }
                                    })()}
                                </ListItemIcon>
                                <ListItemText
                                    style={{ display: (open.collapsed ? 'none' : 'block') }}
                                    primary={x === 'bricata' ? 'OpenText™ NDR' : x[0].toUpperCase() + x.substring(1)}
                                />
                                {panels[x] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </ListItemButton>
                            <Collapse in={panels[x]} timeout="auto" unmountOnExit>
                                <List>
                                    {policies[x] && policies[x].map((v, i) => {
                                        if (policyPerms[v.type].read) {
                                            return (
                                                <React.Fragment key={'policy-sidebar-item-' + i}>
                                                    <ListItemButton className={classes.listItem} onClick={(e) => { handleUpdatePanels(x, v.type) }} aria-label={'Expand or collpase ' + v.name + ' sub-menu'}>
                                                        <ListItemIcon className={classes.widthFix}></ListItemIcon>
                                                        <ListItemText className={classes.hightlightPrimary} primary={POLICY_NAMES[v.type]} />
                                                        {panels[v.type] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </ListItemButton>
                                                    <Collapse in={panels[v.type]} timeout="auto" unmountOnExit>
                                                        <List>
                                                            {v.policies.map((y, j) => {
                                                                return (
                                                                    <ListItem key={'policy-subitem-' + j}>
                                                                        <ListItemIcon className={classes.widthFix}></ListItemIcon>
                                                                        <Avatar className={classes.policyColor} style={{ background: y.color || '#777' }}>{''}</Avatar>
                                                                        <ListItemText
                                                                            className={classes.policyText}
                                                                            classes={{
                                                                                primary: classes.policyTextPrimary,
                                                                                secondary: classes.policyTextSecondary,
                                                                            }}
                                                                            primary={y.name}
                                                                            secondary={'Sensors: ' + y.count}
                                                                            onClick={() => { handleSelectPolicy(v.type, y.name) }}
                                                                        />
                                                                        {policyPerms[v.type].admin && <ListItemSecondaryAction onClick={(e) => { handleOpenPolicySidebarMenu(e, v.type, y.name) }}>
                                                                            <IconButton className={classes.PolicySecondaryAction} size="large"><MenuIcon /></IconButton>
                                                                        </ListItemSecondaryAction>}
                                                                    </ListItem>
                                                                );
                                                            })}
                                                        </List>
                                                    </Collapse>
                                                </React.Fragment>
                                            );
                                        }
                                        return null;
                                    })}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    ) : null;
                })}
            </List>
            <Menu
                anchorEl={menuAnchor}
                keepMounted
                open={Boolean(menuAnchor)}
                onClose={handleClosePolicySudebarMenu}
            >
                <MenuItem onClick={handleAddToFiltered}>Add to sensors in filter</MenuItem>
                <MenuItem onClick={handleAddToAll}>Add to all sensors</MenuItem>
            </Menu>
        </Drawer>
    );
};

PolicySidebar.propTypes = {
    policies: PropTypes.array.isRequired,
    onSelectPolicy: PropTypes.func.isRequired,
    onAddToAll: PropTypes.func.isRequired,
    onAddToFiltered: PropTypes.func.isRequired,
};

export default PolicySidebar;
